import React, { useState } from 'react';
import { 
  Button, 
  Input, 
  Card, 
  Row, 
  Col, 
  Table, 
  Typography, 
  Space,
  Alert,
  message
} from 'antd';
import { SearchOutlined, DatabaseOutlined } from '@ant-design/icons';
import API from '../services/api'; // 导入统一的API实例
import usePermissionCheck from '../hooks/usePermissionCheck';
import PermissionDenied from '../components/PermissionDenied';

const { Title } = Typography;

const UserConnectionQuery = () => {
  const { hasPermission, isLoading: permissionLoading, userAccountType } = usePermissionCheck('canViewIPConnections');
  
  const [ip, setIp] = useState('');
  const [inputData, setInputData] = useState(null);
  const [loading, setLoading] = useState(false); // 控制加载动画


  // 权限检查
  if (!hasPermission) {
    return (
      <PermissionDenied 
        isLoading={permissionLoading}
        permissionName="IP连接"
        userAccountType={userAccountType}
      />
    );
  }

  // 查询单个IP输入包数和流量数据
  const handleIpConnectionQuery = async () => {
    if (!ip.trim()) {
      message.warning('请输入IP地址');
      return;
    }

    setLoading(true); // 开启加载动画
    try {
      const response = await API.get(`/api/user/connections/${ip}`);
      if (!response.data.inputData) {
        message.info('暂无数据');
        setInputData(null);
      } else {
        setInputData(response.data.inputData); // 获取的输入包数和流量数据
        message.success('查询成功');
      }
    } catch (error) {
      console.error('获取单个IP连接数失败:', error);

      // 判断后端是否返回了具体的错误信息，并显示该错误
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message); // 显示后端返回的错误信息
      } else {
        message.error('获取单个IP连接数失败'); // 显示默认错误信息
      }
      setInputData(null);
    } finally {
      setLoading(false); // 结束加载动画
    }
  };

  // 将字节流量转换为 Mbps
  const convertToMbps = (size) => {
    return (size / (1000 * 128)).toFixed(2); // 直接转换为 Mbps
  };

  // 输入数据表格列定义
  const inputColumns = [
    {
      title: '序号',
      key: 'index',
      align: 'center',
      width: 50,
      render: () => 1,
    },
    {
      title: '主机',
      dataIndex: 'host',
      key: 'host',
      align: 'center',
      width: 100,
    },
    {
      title: '输入包数(pps)',
      dataIndex: 'in_packets',
      key: 'in_packets',
      align: 'center',
      width: 90,
    },
    {
      title: '输入流量(Mbps)',
      dataIndex: 'in_size',
      key: 'in_size',
      align: 'center',
      width: 100,
      render: (value) => convertToMbps(value),
    },
    {
      title: '输入拦截流量(Mbps)',
      dataIndex: 'in_drop_size',
      key: 'in_drop_size',
      align: 'center',
      width: 110,
      render: (value) => convertToMbps(value),
    },
    {
      title: 'UDP',
      dataIndex: 'udp_packets',
      key: 'udp_packets',
      align: 'center',
      width: 60,
    },
    {
      title: 'ICMP',
      dataIndex: 'icmp_packets',
      key: 'icmp_packets',
      align: 'center',
      width: 60,
    },
    {
      title: 'TCP',
      dataIndex: 'tcp_packets',
      key: 'tcp_packets',
      align: 'center',
      width: 60,
    },
    {
      title: '其他',
      dataIndex: 'other_packets',
      key: 'other_packets',
      align: 'center',
      width: 60,
    },
  ];

  // 输出数据表格列定义
  const outputColumns = [
    {
      title: '序号',
      key: 'index',
      align: 'center',
      width: 50,
      render: () => 1,
    },
    {
      title: '主机',
      dataIndex: 'host',
      key: 'host',
      align: 'center',
      width: 100,
    },
    {
      title: '输出包数(pps)',
      dataIndex: 'out_packets',
      key: 'out_packets',
      align: 'center',
      width: 90,
    },
    {
      title: '输出流量(Mbps)',
      dataIndex: 'out_size',
      key: 'out_size',
      align: 'center',
      width: 100,
      render: (value) => convertToMbps(value),
    },
    {
      title: '输出拦截流量(Mbps)',
      dataIndex: 'out_drop_size',
      key: 'out_drop_size',
      align: 'center',
      width: 110,
      render: (value) => convertToMbps(value),
    },
    {
      title: 'UDP',
      dataIndex: 'udp_packets',
      key: 'udp_packets',
      align: 'center',
      width: 60,
    },
    {
      title: 'ICMP',
      dataIndex: 'icmp_packets',
      key: 'icmp_packets',
      align: 'center',
      width: 60,
    },
    {
      title: 'TCP',
      dataIndex: 'tcp_packets',
      key: 'tcp_packets',
      align: 'center',
      width: 60,
    },
    {
      title: '其他',
      dataIndex: 'other_packets',
      key: 'other_packets',
      align: 'center',
      width: 60,
    },
  ];



  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 标题和说明 */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 20, color: '#1890ff' }}>
            连接数查询
          </Title>
          
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Alert
              message="此功能仅限云主机和托管服务器的IP使用"
              type="info"
              showIcon
              style={{ 
                maxWidth: 600, 
                textAlign: 'center',
                border: '1px solid #91d5ff',
                borderRadius: 6
              }}
            />
          </div>
        </div>

        {/* 查询表单 */}
        <Row justify="center">
          <Col xs={24} sm={18} md={14} lg={10} xl={8}>
            <Card>
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    输入 IP 地址
                  </label>
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      value={ip}
                      onChange={(e) => setIp(e.target.value)}
                      placeholder="输入查询IP地址"
                      onPressEnter={handleIpConnectionQuery}
                      prefix={<SearchOutlined />}
                    />
                    <Button 
                      type="primary" 
                      onClick={handleIpConnectionQuery} 
                      loading={loading}
                      icon={!loading && <SearchOutlined />}
                    >
                      查询
                    </Button>
                  </Space.Compact>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* 数据表格 */}
        {inputData && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* 输入数据表格 */}
            <Row justify="center">
              <Col xs={24} sm={20} md={18} lg={16} xl={14}>
                <Card 
                  title={
                    <span style={{ color: '#1890ff' }}>
                      <DatabaseOutlined style={{ marginRight: 8 }} />
                      输入数据
                    </span>
                  }
                >
                  <Table
                    columns={inputColumns}
                    dataSource={[inputData]}
                    rowKey="host"
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 590 }}
                  />
                </Card>
              </Col>
            </Row>

            {/* 输出数据表格 */}
            <Row justify="center">
              <Col xs={24} sm={20} md={18} lg={16} xl={14}>
                <Card 
                  title={
                    <span style={{ color: '#52c41a' }}>
                      <DatabaseOutlined style={{ marginRight: 8 }} />
                      输出数据
                    </span>
                  }
                >
                  <Table
                    columns={outputColumns}
                    dataSource={[inputData]}
                    rowKey="host"
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 590 }}
                  />
                </Card>
              </Col>
            </Row>
          </Space>
        )}
      </Space>
    </div>
  );
};

export default UserConnectionQuery;
